import * as i0 from '@angular/core';
import { inject, Directive, Component, ViewEncapsulation, ChangeDetectionStrategy, NgModule } from '@angular/core';
import { ButtonComponent, ButtonVariant } from '@odx/angular/components/button';
import { __decorate } from 'tslib';
import { LogoDirective } from '@odx/angular/components/logo';
import { CSSComponent } from '@odx/angular/internal';
import { injectElement } from '@odx/angular/utils';
import { CoreModule } from '@odx/angular';
import { ActionGroupComponent } from '@odx/angular/components/action-group';
import { AvatarComponent } from '@odx/angular/components/avatar';
import { IconComponent } from '@odx/angular/components/icon';
import { MainMenuModule } from '@odx/angular/components/main-menu';

/**
 * Directive for the header avatar component.
 * This directive is responsible for customizing the appearance of the header avatar button.
 */
class HeaderAvatarDirective {
    constructor() {
        this.button = inject(ButtonComponent, { self: true });
    }
    ngAfterViewInit() {
        this.button.variant = ButtonVariant.GHOST;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: HeaderAvatarDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.13", type: HeaderAvatarDirective, isStandalone: true, selector: "button[odxButton][odxHeaderAvatar]", host: { properties: { "class.odx-header__avatar": "true" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: HeaderAvatarDirective, decorators: [{
            type: Directive,
            args: [{
                    standalone: true,
                    selector: 'button[odxButton][odxHeaderAvatar]',
                    host: {
                        '[class.odx-header__avatar]': 'true',
                    },
                }]
        }] });

/**
 * Directive for displaying the title in the header component.
 */
class HeaderTitleDirective {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: HeaderTitleDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "18.2.13", type: HeaderTitleDirective, isStandalone: true, selector: "odx-header-title", host: { properties: { "class.odx-header__title": "true" } }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: HeaderTitleDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'odx-header-title',
                    standalone: true,
                    host: {
                        '[class.odx-header__title]': 'true',
                    },
                }]
        }] });

/**
 * Represents the header component.
 */
let HeaderComponent = class HeaderComponent {
    constructor() {
        this.element = injectElement();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: HeaderComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: HeaderComponent, isStandalone: true, selector: "odx-header", ngImport: i0, template: "<ng-content select=\"button[odxMainMenuButton]\" />\n<odx-logo />\n<ng-content select=\"odx-header-title\" />\n<div class=\"odx-header__content\">\n  <span class=\"odx-header__content-container\"><ng-content /></span>\n</div>\n<ng-content select=\"odx-search-bar\" />\n<ng-content select=\"odx-action-group, odx-auth\" />\n<ng-content select=\"[odxHeaderAvatar]\" />\n", dependencies: [{ kind: "directive", type: LogoDirective, selector: "odx-logo", inputs: ["size", "variant"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None }); }
};
HeaderComponent = __decorate([
    CSSComponent('header')
], HeaderComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: HeaderComponent, decorators: [{
            type: Component,
            args: [{ standalone: true, selector: 'odx-header', imports: [LogoDirective], encapsulation: ViewEncapsulation.None, changeDetection: ChangeDetectionStrategy.OnPush, template: "<ng-content select=\"button[odxMainMenuButton]\" />\n<odx-logo />\n<ng-content select=\"odx-header-title\" />\n<div class=\"odx-header__content\">\n  <span class=\"odx-header__content-container\"><ng-content /></span>\n</div>\n<ng-content select=\"odx-search-bar\" />\n<ng-content select=\"odx-action-group, odx-auth\" />\n<ng-content select=\"[odxHeaderAvatar]\" />\n" }]
        }] });

const modules = [
    HeaderComponent,
    HeaderTitleDirective,
    HeaderAvatarDirective,
    MainMenuModule,
    ActionGroupComponent,
    IconComponent,
    ButtonComponent,
    AvatarComponent,
];
class HeaderModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: HeaderModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: HeaderModule, imports: [HeaderComponent,
            HeaderTitleDirective,
            HeaderAvatarDirective,
            MainMenuModule,
            ActionGroupComponent,
            IconComponent,
            ButtonComponent,
            AvatarComponent], exports: [CoreModule, HeaderComponent,
            HeaderTitleDirective,
            HeaderAvatarDirective,
            MainMenuModule,
            ActionGroupComponent,
            IconComponent,
            ButtonComponent,
            AvatarComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: HeaderModule, imports: [MainMenuModule,
            ActionGroupComponent,
            IconComponent,
            ButtonComponent,
            AvatarComponent, CoreModule, MainMenuModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: HeaderModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: modules,
                    exports: [CoreModule, ...modules],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { HeaderAvatarDirective, HeaderComponent, HeaderModule, HeaderTitleDirective };
